import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Play, ArrowRight, Clock, Shield, Zap, CheckCircle2, FileText, BarChart3, Sparkles } from "lucide-react";

const journeySteps = [
  {
    time: "Minute 1",
    title: "Submit your enquiry",
    icon: Zap,
    desc: "Tell us about your organisation in a 60-second form. Our team reviews business-domain enquiries the same business day and provisions a guided sandbox preloaded at ~80% operational maturity (48 assets · 22 BIAs · 22 risks · 12 recovery plans · 20 exercises).",
    highlight: true,
  },
  {
    time: "Hour 1",
    title: "Your First BIA Complete",
    icon: Shield,
    desc: "AI Reconnaissance scans your website to pre-suggest departments and facilities. The 5-step BIA Wizard captures impact, dependencies, and inheritance — RTO / RPO / MTPD derived automatically.",
    highlight: true,
  },
  {
    time: "Day 1",
    title: "Approvals & Programme Health Live",
    icon: BarChart3,
    desc: "Risk Assessment heat-map, Recovery Plans (auto-stubbed from approved BIAs), and the Performance &amp; Compliance dashboard with ISO 22301 clause-level scoring — all live the moment you submit.",
    highlight: true,
  },
  {
    time: "Week 1",
    title: "Scale Across Your Org",
    icon: FileText,
    desc: "Federate Microsoft 365 + Workday so people, titles, departments and locations flow nightly. Run exercises, export audit-grade PDFs, prove ROI to leadership.",
    highlight: false,
  },
];

export default function TrialJourneySection() {
  return (
    <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-zinc-50" data-testid="trial-journey-section">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-100 text-sky-700 text-sm font-semibold mb-6">
            <Sparkles className="w-4 h-4" />
            Speed to Value
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-5">
            Audit-Ready Compliance.{" "}
            <span className="text-sky-700">Day One.</span>
          </h2>
          <p className="text-base sm:text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Most BCM tools take months to implement. ResiliencyHUB delivers your first compliance score in hours —
            then gives you a turnkey pilot to scale it across your organization.
          </p>
        </div>

        {/* Journey Timeline */}
        <div className="grid md:grid-cols-4 gap-6 mb-14">
          {journeySteps.map((step, i) => (
            <div key={i} data-testid={`journey-step-${i}`} className="relative">
              {/* Connector line */}
              {i < journeySteps.length - 1 && (
                <div className="hidden md:block absolute top-8 left-[calc(50%+24px)] w-[calc(100%-48px)] h-0.5 bg-sky-200 z-0" />
              )}
              <div className={`relative bg-white rounded-2xl border p-6 shadow-sm hover:shadow-lg transition-all duration-300 z-10 ${step.highlight ? "border-sky-200 hover:border-sky-400" : "border-slate-200 hover:border-sky-300"}`}>
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ring-4 ${step.highlight ? "bg-sky-700 ring-sky-700/15" : "bg-slate-400 ring-slate-400/10"}`}>
                  <step.icon className="w-6 h-6 text-white" />
                </div>
                <p className={`text-xs font-bold tracking-[0.15em] uppercase mb-1 ${step.highlight ? "text-sky-700" : "text-slate-400"}`}>{step.time}</p>
                <h4 className="text-lg font-bold text-slate-900 mb-2">{step.title}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* What you get + CTA */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
          <div className="grid lg:grid-cols-2">
            {/* Left: What's included */}
            <div className="p-8 sm:p-10">
              <h3 className="text-xl font-bold text-slate-900 mb-2">Everything Included. Full Platform Access.</h3>
              <p className="text-sm text-slate-500 mb-6">BCM Manager approval grants unrestricted access — zero feature gates, including enterprise Federation.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  "Unlimited team members",
                  "Unlimited departments",
                  "Unlimited BIA processes",
                  "AI-powered insights & generation",
                  "Unlimited recovery plans",
                  "Risk assessments + inheritance",
                  "Compliance dashboard",
                  "PDF report exports",
                  "AI onboarding guide",
                  "ISO 22301 mapping",
                  "O365 / Workday / ServiceNow federation",
                  "Geospatial asset intelligence",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5 text-sm text-slate-700">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
            </div>

            {/* Right: CTA */}
            <div className="bg-slate-950 p-8 sm:p-10 flex flex-col justify-center">
              <p className="text-xs font-bold tracking-[0.15em] uppercase text-emerald-400 mb-3">See Results Today</p>
              <h3 className="text-2xl font-bold text-white mb-4">Your Compliance Score Is Waiting</h3>
              <p className="text-sm text-slate-400 mb-6 leading-relaxed">
                Request access for your BCM team — our team reviews enquiries within one business day and provisions a guided sandbox tailored to your organisation.
              </p>
              <div className="flex flex-col gap-3">
                <Link to="/request-demo">
                  <Button className="w-full bg-sky-700 hover:bg-sky-800 h-12 text-base font-semibold shadow-lg shadow-sky-700/20" data-testid="journey-contact-btn">
                    Request Enquiry &amp; Demo
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
